import { isMultiValued, isSortable, type Sort } from '@servicetim/shared';
import { Button } from '@servicetim/ui/components';
import { Plus, Trash2 } from 'lucide-react';
import type { CatalogField, FieldCatalog } from '../../projects/catalog/field-catalog';
import { fieldOptions } from '../../projects/conditions/field-condition-row';
import { moveItem, removeAt, replaceAt } from '../../projects/lib/list-utils';
import { ReorderButtons } from '../../projects/ui/reorder-buttons';
import { SimpleSelect } from '../../projects/ui/simple-select';

export const MAX_SORTS = 3;

/** Mirrors the domain validator: registry-sortable types, and custom fields only when indexed. */
export const canSortBy = (f: CatalogField): boolean =>
  f.type !== 'text_search' && isSortable(f.type) && (f.system || f.indexed) && !f.archived;

/** Mirrors the domain validator: not text search, not long text, not multi-valued. */
export const canGroupBy = (f: CatalogField): boolean =>
  f.type !== 'text_search' && f.type !== 'long_text' && !isMultiValued(f.type) && !f.archived;

export interface SortEditorProps {
  value: readonly Sort[];
  onChange: (next: Sort[]) => void;
  catalog: FieldCatalog;
  idPrefix: string;
}

/** Up to three sort keys; `id` is always appended server-side as the tiebreaker. */
export function SortEditor({ value, onChange, catalog, idPrefix }: SortEditorProps) {
  const options = fieldOptions(catalog, canSortBy);
  return (
    <div className="space-y-2">
      {value.length === 0 ? (
        <p className="text-fg-muted text-sm">Default order: last updated first.</p>
      ) : (
        <ul className="space-y-2">
          {value.map((sort, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: rows carry no identity; their position is the key
            <li key={`${idPrefix}-${index}`} className="flex flex-wrap items-center gap-2">
              <span className="w-5 text-right text-fg-muted text-xs tabular-nums">{index + 1}</span>
              <SimpleSelect
                aria-label={`Sort field ${index + 1}`}
                size="sm"
                className="w-56"
                value={sort.field}
                options={options}
                onChange={(next) =>
                  next && onChange(replaceAt(value, index, { ...sort, field: next }))
                }
              />
              <SimpleSelect
                aria-label={`Sort direction ${index + 1}`}
                size="sm"
                className="w-36"
                value={sort.dir}
                options={[
                  { value: 'asc', label: 'Ascending' },
                  { value: 'desc', label: 'Descending' },
                ]}
                onChange={(next) =>
                  next && onChange(replaceAt(value, index, { ...sort, dir: next as Sort['dir'] }))
                }
              />
              <ReorderButtons
                label={`sort ${index + 1}`}
                onUp={index > 0 ? () => onChange(moveItem(value, index, index - 1)) : undefined}
                onDown={
                  index < value.length - 1
                    ? () => onChange(moveItem(value, index, index + 1))
                    : undefined
                }
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Remove sort ${index + 1}`}
                onClick={() => onChange(removeAt(value, index))}
              >
                <Trash2 />
              </Button>
            </li>
          ))}
        </ul>
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={value.length >= MAX_SORTS || options.length === 0}
        onClick={() =>
          onChange([
            ...value,
            { field: (options[0]?.value ?? 'updated_at') as string, dir: 'desc' },
          ])
        }
      >
        <Plus />
        Add sort key
      </Button>
    </div>
  );
}
